import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import api, { setToken } from "@/lib/api";
import { LeadsPanel, EnquiriesPanel, SubscribersPanel, OffersPanel, ClicksPanel, JourneysPanel, AccessControlPanel, IpsPanel } from "./panels";

const TABS = [
  { id: "leads", label: "Leads" },
  { id: "enquiries", label: "Enquiries" },
  { id: "subscribers", label: "Subscribers" },
  { id: "offers", label: "Offers & Tracking" },
  { id: "clicks", label: "Click Logs" },
  { id: "journeys", label: "User Journeys" },
  { id: "access", label: "Access Control" },
  { id: "ips", label: "Blocked IPs" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setAdmin(r.data);
        loadStats();
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate, loadStats]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setToken(null);
      navigate("/admin/login");
    }
  };

  if (!admin) {
    return (
      <div className="min-h-screen bg-[#F6F7F3] flex items-center justify-center">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#5C6B62]">Authenticating…</p>
      </div>
    );
  }

  const cards = stats
    ? [
        { label: "Total Leads", value: stats.leads, tid: "stat-leads" },
        { label: "Leads · 7 Days", value: stats.leads_7d, tid: "stat-leads-7d" },
        { label: "Enquiries", value: stats.enquiries, tid: "stat-enquiries" },
        { label: "VIP Subscribers", value: stats.subscribers, tid: "stat-subscribers" },
        { label: "Tracked Clicks", value: stats.clicks, tid: "stat-clicks" },
        { label: "Live Offers", value: stats.offers_active, tid: "stat-offers" },
        { label: "Blocked IPs", value: stats.blocked_ips, tid: "stat-blocked" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#F6F7F3] text-[#0F1F1A]" data-testid="admin-dashboard">
      <header className="border-b border-[#DFE5DC] backdrop-blur-2xl bg-white/70 sticky top-0 z-40">
        <div className="px-6 h-16 flex items-center justify-between">
          <p className="font-display text-2xl tracking-tight text-[#0F1F1A]">
            Trusted <span className="italic text-[#0C6B4F]">Financial Hub</span>
            <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A978D] ml-4 not-italic">Admin</span>
          </p>
          <div className="flex items-center gap-5">
            <span className="hidden sm:inline font-mono2 text-xs text-[#5C6B62]" data-testid="admin-email">{admin.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C6B62] hover:text-[#D92D20] transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-[#DFE5DC] border border-[#DFE5DC] mb-8" data-testid="admin-stats">
          {cards.map((c) => (
            <div key={c.tid} className="bg-white p-5">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A978D] mb-2">{c.label}</p>
              <p className="font-mono2 text-3xl text-[#0C6B4F]" data-testid={c.tid}>{c.value}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition-[background-color,border-color,color] duration-300 ${
                tab === t.id
                  ? "bg-[#0C6B4F] text-white border-[#0C6B4F] font-semibold"
                  : "border-[#DFE5DC] bg-white text-[#5C6B62] hover:text-[#0F1F1A] hover:border-[#CBD5C9]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsPanel onChange={loadStats} />}
        {tab === "enquiries" && <EnquiriesPanel onChange={loadStats} />}
        {tab === "subscribers" && <SubscribersPanel onChange={loadStats} />}
        {tab === "offers" && <OffersPanel onChange={loadStats} />}
        {tab === "clicks" && <ClicksPanel />}
        {tab === "journeys" && <JourneysPanel />}
        {tab === "access" && <AccessControlPanel />}
        {tab === "ips" && <IpsPanel onChange={loadStats} />}
      </main>
    </div>
  );
}
